import { isDefined, translations } from "@timeedit/registration-shared";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { createEvents } from "../../../utils/events";
import { ActivityDescriptionSlotProps } from "../types";

export function AllocateCourseListText({
  registration,
  activityId,
  mode,
}: ActivityDescriptionSlotProps): ReactElement {
  const activity = registration.activities[activityId];
  if (activity?.allocationSignal === "OK") {
    const registeredGroupId = activity.children.find(
      (groupId) =>
        registration.groups[groupId]?.allocationStatus === "ALLOCATED_TO_THIS"
    );
    if (!isDefined(registeredGroupId)) return <>{"-"}</>;

    // We already createEvents in RegistrationCalendar and GroupPage, perhaps reuse calculation here in those components?
    const reservations = (registration.events ?? [])
      .filter(isDefined)
      .filter((event) => activity?.children.includes(event.groupId));
    const events = createEvents({ reservations });

    const eventDates = events
      .reduce<[Date, Date][]>((eventDates, event) => {
        if (event.data.groupId !== registeredGroupId) return eventDates;
        if (event.noSchedule) return eventDates;
        const currentStart = event.start;
        const currentEnd = event.end;
        const sameTimeEachWeek = eventDates.some(
          ([start, end]) =>
            dayjs(start).day() === dayjs(currentStart).day() &&
            dayjs(end).day() === dayjs(currentEnd).day() &&
            dayjs(start).hour() === dayjs(currentStart).hour() &&
            dayjs(end).hour() === dayjs(currentEnd).hour() &&
            dayjs(start).minute() === dayjs(currentStart).minute() &&
            dayjs(end).minute() === dayjs(currentEnd).minute()
        );
        if (sameTimeEachWeek) return eventDates;

        return [...eventDates, [currentStart, currentEnd]];
      }, [])
      .sort((a, b) => {
        const daySort = dayjs(a[0]).day() - dayjs(b[0]).day();
        if (daySort === 0) {
          return dayjs(a[0]).hour() - dayjs(b[0]).hour();
        }
        return daySort;
      });

    return (
      <>
        {...eventDates.map((event, index) => {
          return (
            <div
              key={`${activityId}-${event[0].getDay()}-${index}`}
              style={{ textWrap: "nowrap" }}
            >
              {dayjs(event[0]).format("dddd HH:mm")} -{" "}
              {dayjs(event[1]).format("HH:mm")}
            </div>
          );
        })}
      </>
    );
  }

  const optionAvailable =
    activity?.children.filter((groupId) => {
      const group = registration.groups[groupId];
      if (!isDefined(group)) return false;
      const full =
        mode === "student"
          ? group.seats.available === 0
          : group.seats.raw.total - group.seats.raw.taken <= 0;
      return !full && group.open;
    }) ?? [];

  const openOptions =
    optionAvailable.length === 1
      ? `${translations.optionsAvailableOne} `
      : `${optionAvailable.length} ${translations.optionsAvailable} `;

  const closedOptions =
    activity?.children.length === 0
      ? translations.optionsAvailableNone
      : translations.closed;

  return <>{activity?.open ? openOptions : closedOptions}</>;
}
